import { numberWithCommas } from '@callrail/looky/util';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import {
  PlanDisplayInfo,
  PlatformSpecificDisplayInfo,
} from '../interfaces/plan-display-info';
import { AgentPricing } from '../interfaces/platform-pricing';
import { ZuoraRatePlanState } from '../interfaces/zuora';
import { ProductType } from '../type-aliases/product-type';
import { DiscountItem } from '../interfaces/discount-info.interface';
import {
  UsagePricingChargeOverage,
  UsagePricingChargePerUnit,
  UsagePricingChargeVolume,
} from '../interfaces/usage_pricing';

/** Representing an agency's current billing platforms/plans.
 */
export abstract class PlatformModel {
  public dayjs = dayjs;

  public id: string;
  public name: string;
  public price: number;
  public discounted_price: number;
  public discount_information: DiscountItem[];
  public currency: string;
  public customized_plan: boolean;
  /**
   * The time period that the subscription will be billed on.
   */
  public billing_period: 'Annual' | 'Month';
  /** Returns the state of whether this platform will be a trial or not. */
  public eligible_for_trial: boolean;
  /** Returns the state of whether this platform is a trial or not. */
  public trial: boolean;
  public state: ZuoraRatePlanState;
  public product_tier: 'Base' | 'Premium' | string;
  public standalone: boolean;

  /**
   * The date that the subscription's trial will expire on.
   * Based on Eastern Time Zone.
   *
   * ex: '2019-02-15'
   */
  public trial_expiration: string;
  public trial_expiration_date: string;

  /** The number of days left in a trial platform based on the trial expires date. */
  public daysInTrial: number;
  public eligibleTrialDays: number;
  public activeTrial: boolean;

  /**
   * The date that the platform has already been charged through for this subscription
   */
  public charged_through: string;
  public agents: AgentPricing;

  public formattedBillingDate;
  public validBillingDate = false;

  public feature_trial?: {
    activated_at: string;
    expires_at: string;
    price_diff: number;
    convert_plan: boolean;
  };

  private _daysInTrial: number;
  private _activeTrial: boolean;
  private _mToday: dayjs.Dayjs;
  private _mExpiration: dayjs.Dayjs;
  private _calculatedExpiration: dayjs.Dayjs;

  public abstract platform: ProductType;

  /**
   * Displays general info about a platform.
   */
  public abstract platformInfo: PlatformSpecificDisplayInfo;

  /**
   * Loose object that holds various sentences/text that can be used to display
   * As you need more sentences, add them to the displayText object.
   */
  public abstract displayText: { [key: string]: string };

  public charges: (
    | UsagePricingChargeOverage
    | UsagePricingChargePerUnit
    | UsagePricingChargeVolume
  )[];

  constructor(platformPlan) {
    this.dayjs.extend(isSameOrAfter);
    Object.assign(this, platformPlan);
    this._mToday ||= this.dayjs().startOf('day');
    this._mExpiration ||= this.dayjs(this.trial_expiration);
    this._calculatedExpiration ||= this.dayjs(this.trial_expiration_date);
    this.setDaysInTrial();
    this.setEligibleTrialDays();
    this.setActiveTrial();
    this.setBillingDate();
  }

  // These getters are important for inheriting classes to have because they're used in setting
  // the displayInfo property. Getters are able to be called before the constructor of a child
  // has been called, which makes them better than static attributes in that regard. However,
  // having static attributes to reference in UI templates is better for performance. Since these
  // attributes are in here as UI helpers, we want to only use these for initialization
  /**
   * Please reference `displayInfo.usageInfo` instead
   */
  public abstract get _usageInfo(): string[];
  /**
   * Please reference `displayInfo.unitPrice` instead
   */
  public abstract get _unitPrice(): number;

  /**
   * Commonly needed info to display that is plan specific.
   *
   * Note: this is a getter, so it should NOT be used directly in the template
   */
  public get displayInfo(): PlanDisplayInfo {
    return {
      usageInfo: this._usageInfo.map((usage) => numberWithCommas(usage)),
      unitPrice: this._unitPrice,
    };
  }

  public get isAnnual(): boolean {
    return this.billing_period === 'Annual';
  }

  public setBillingDate() {
    if (
      this.trial_expiration == null ||
      this.dayjs(this.trial_expiration).isBefore(this.dayjs())
    ) {
      this.formattedBillingDate = 'immediately';
      this.validBillingDate = false;
    } else {
      this.formattedBillingDate = this.dayjs(this.trial_expiration).format(
        'MMM D, YYYY'
      );
      this.validBillingDate = true;
    }
  }

  private setDaysInTrial() {
    this._daysInTrial ||= this.dayjs(this._mExpiration).diff(
      this._mToday,
      'days'
    );
    this.daysInTrial = this.trial ? this._daysInTrial : null;
  }

  private setEligibleTrialDays() {
    this.eligibleTrialDays ||=
      this.dayjs(this._calculatedExpiration).diff(this._mToday, 'days') - 1;
  }

  private setActiveTrial() {
    this._activeTrial ||= this._mExpiration.isSameOrAfter(this._mToday, 'days');
    this.activeTrial = this.trial ? this._activeTrial : false;
  }
}
